import React, { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { propertiesAPI } from '../api';
import { useToast } from '../hooks/use-toast';
import MediaPicker from '../components/MediaPicker';
import { ArrowLeft, Plus, Trash2, Pencil, Star, Home, MapPin, Library, FileSpreadsheet, Download, RotateCcw, BedDouble, RefreshCcw } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const FALLBACK_IMAGE = '/api/files/paisley-org/wp-content/uploads/2025/09/Paisley-skyline-from-abbey-770x513-1.jpg';

const EMPTY = {
  title: '', address: '', area: '', bedrooms: '', property_type: 'flat',
  furnished: '', monthly_rent: '', description: '',
  image_url: '', image_url_2: '', listing_url: '', agent: '', source_site: '',
  contact_phone: '', contact_email: '', available_from: '',
  featured: false, published: true, slug: '',
};

const PROPERTY_TYPES = ['flat', 'house', 'studio', 'room', 'other'];

const PropertiesManager = () => {
  const { toast } = useToast();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(EMPTY);
  const [saving, setSaving] = useState(false);
  const [csvBusy, setCsvBusy] = useState(false);
  const [rightmoveBusy, setRightmoveBusy] = useState(false);
  const csvRef = useRef(null);
  const [mediaOpen, setMediaOpen] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const r = await propertiesAPI.adminList();
      // Active first, then expired
      const sorted = (r.data || []).sort((a, b) => {
        if (a.active !== b.active) return a.active ? -1 : 1;
        return new Date(b.created_at) - new Date(a.created_at);
      });
      setItems(sorted);
    } catch (e) {
      toast({ title: 'Failed to load properties', variant: 'destructive' });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const startNew = () => { setEditing({}); setForm(EMPTY); };
  const startEdit = (p) => {
    setEditing(p);
    setForm({
      ...EMPTY,
      ...p,
      bedrooms: p.bedrooms ?? '',
      monthly_rent: p.monthly_rent ?? '',
      available_from: p.available_from ? p.available_from.slice(0, 10) : '',
    });
  };
  const cancelEdit = () => { setEditing(null); setForm(EMPTY); };

  const save = async (e) => {
    e.preventDefault();
    if (!form.title.trim()) {
      toast({ title: 'Title is required', variant: 'destructive' });
      return;
    }
    setSaving(true);
    try {
      const payload = {
        ...form,
        slug: form.slug || undefined,
        bedrooms: form.bedrooms === '' ? null : parseInt(form.bedrooms, 10),
        monthly_rent: form.monthly_rent === '' ? null : parseFloat(form.monthly_rent),
        available_from: form.available_from || null,
      };
      if (editing && editing.id) {
        await propertiesAPI.update(editing.id, payload);
        toast({ title: 'Property updated' });
      } else {
        await propertiesAPI.create(payload);
        toast({ title: 'Property created' });
      }
      cancelEdit();
      load();
    } catch (e2) {
      toast({ title: 'Save failed', description: e2.response?.data?.detail || e2.message, variant: 'destructive' });
    } finally {
      setSaving(false);
    }
  };

  const remove = async (p) => {
    if (!window.confirm(`Delete "${p.title}"?`)) return;
    try {
      await propertiesAPI.delete(p.id);
      toast({ title: 'Deleted' });
      load();
    } catch (_) {
      toast({ title: 'Delete failed', variant: 'destructive' });
    }
  };

  const reactivate = async (p) => {
    try {
      await propertiesAPI.update(p.id, { active: true });
      toast({ title: 'Property re-activated', description: 'Expiry extended by 10 days.' });
      load();
    } catch (_) {
      toast({ title: 'Re-activate failed', variant: 'destructive' });
    }
  };

  const handleCsv = async (file) => {
    if (!file) return;
    setCsvBusy(true);
    try {
      const fd = new FormData();
      fd.append('file', file);
      const r = await propertiesAPI.importCSV(fd);
      const { created, skipped = 0, errors = [] } = r.data;
      const parts = [`${created} created`];
      if (skipped) parts.push(`${skipped} skipped (duplicate)`);
      if (errors.length) parts.push(`${errors.length} errors`);
      toast({ title: 'Properties imported', description: parts.join(' · ') });
      if (errors.length) console.warn('CSV import errors:', errors);
      if (skipped) console.info('CSV duplicates skipped:', r.data.skipped_items);
      load();
    } catch (e) {
      toast({ title: 'CSV import failed', description: e.response?.data?.detail || e.message, variant: 'destructive' });
    } finally {
      setCsvBusy(false);
      if (csvRef.current) csvRef.current.value = '';
    }
  };

  const handleRightmoveSync = async () => {
    if (rightmoveBusy) return;
    setRightmoveBusy(true);
    toast({ title: 'Fetching from Rightmove + OnTheMarket…', description: 'This takes 30-60s while pages are scraped.' });
    try {
      const r = await propertiesAPI.rentalsSyncAll();
      const c = r.data.combined || {};
      toast({
        title: 'Rental sync complete',
        description: `Rightmove: ${r.data.rightmove?.fetched || 0} · OnTheMarket: ${r.data.onthemarket?.fetched || 0} · ${c.created || 0} new · ${c.refreshed || 0} refreshed`,
      });
      load();
    } catch (e) {
      toast({ title: 'Rental sync failed', description: e.response?.data?.detail || e.message, variant: 'destructive' });
    } finally {
      setRightmoveBusy(false);
    }
  };

  const downloadTemplate = () => {
    const csv = 'Address,Agent/Landlord,Area,Bedrooms,Contact Email,Contact Phone,Date Found,Dedup Key,Furnished,Image URL 1,Image URL 2,Last Updated,Listing URL,Monthly Rent (£),Property Type,Source Site\n' +
                '"Queen Street, Paisley, Renfrewshire, PA1",Castle Residential,PA1,2,,0141 673 4224,"May 22, 2026",,Unknown,https://example.com/image.jpg,,,"https://example.com/listing",695,Flat,Rightmove\n';
    const blob = new Blob([csv], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'paisley-properties-template.csv';
    a.click();
    URL.revokeObjectURL(url);
  };

  return (
    <div className="min-h-screen bg-slate-50">
      <header className="bg-white border-b border-gray-200">
        <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between flex-wrap gap-3">
          <div className="flex items-center gap-4">
            <Link to="/admin/dashboard">
              <Button variant="outline" size="sm"><ArrowLeft size={16} className="mr-2" />Dashboard</Button>
            </Link>
            <h1 className="text-2xl font-bold text-slate-900">Properties Manager</h1>
          </div>
          <div className="flex gap-2 flex-wrap">
            <Button type="button" variant="outline" onClick={downloadTemplate} data-testid="properties-csv-template-btn">
              <Download size={14} className="mr-2" />CSV template
            </Button>
            <input ref={csvRef} type="file" accept=".csv" className="hidden" onChange={(e) => handleCsv(e.target.files?.[0])} />
            <Button type="button" variant="outline" disabled={csvBusy} onClick={() => csvRef.current?.click()} data-testid="properties-csv-import-btn">
              <FileSpreadsheet size={14} className="mr-2" />{csvBusy ? 'Importing…' : 'Import CSV'}
            </Button>
            <Button type="button" variant="outline" disabled={rightmoveBusy} onClick={handleRightmoveSync} data-testid="properties-rightmove-sync-btn" title="Fetches Paisley (PA1-3) rentals from Rightmove and OnTheMarket. Daily automatic sync runs at 12:00.">
              <RefreshCcw size={14} className={`mr-2 ${rightmoveBusy ? 'animate-spin' : ''}`} />{rightmoveBusy ? 'Syncing…' : 'Refresh rentals'}
            </Button>
            <Button type="button" onClick={startNew} className="bg-emerald-600 hover:bg-emerald-700" data-testid="new-property-btn">
              <Plus size={14} className="mr-2" />New property
            </Button>
          </div>
        </div>
      </header>

      <div className="max-w-7xl mx-auto px-6 py-8 grid lg:grid-cols-[1fr_420px] gap-6">
        {/* List */}
        <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
          <div className="px-5 py-3 border-b border-gray-100 text-sm font-semibold text-slate-700 flex justify-between">
            <span>{items.length} listing{items.length === 1 ? '' : 's'} total</span>
            <span className="text-xs text-gray-500 font-normal">Listings expire after 10 days</span>
          </div>
          {loading ? (
            <div className="p-8 text-center text-gray-400">Loading…</div>
          ) : items.length === 0 ? (
            <div className="p-12 text-center">
              <Home size={32} className="mx-auto text-gray-300 mb-3" />
              <p className="text-gray-600 mb-4">No properties yet. Add your first one or import a CSV.</p>
              <Button onClick={startNew} className="bg-emerald-600 hover:bg-emerald-700">
                <Plus size={14} className="mr-2" />Create first property
              </Button>
            </div>
          ) : (
            <ul className="divide-y divide-gray-100" data-testid="properties-admin-list">
              {items.map((p) => (
                <li key={p.id} className="px-5 py-4 hover:bg-slate-50 flex items-center gap-4" data-testid={`admin-property-row-${p.slug}`}>
                  <div className="shrink-0 w-16 h-16 rounded-lg overflow-hidden bg-slate-100">
                    <img
                      src={p.image_url ? (p.image_url.startsWith('http') ? p.image_url : `${BACKEND_URL}${p.image_url}`) : `${BACKEND_URL}${FALLBACK_IMAGE}`}
                      alt=""
                      className="w-full h-full object-cover"
                      onError={(e) => { if (!e.target.dataset.f) { e.target.dataset.f = '1'; e.target.src = `${BACKEND_URL}${FALLBACK_IMAGE}`; } }}
                    />
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-semibold text-slate-900 truncate flex items-center gap-1.5">
                      {p.featured && <Star size={12} className="text-fuchsia-500 fill-fuchsia-500" />}
                      {p.title}
                      {!p.published && <span className="text-[10px] text-amber-700 bg-amber-100 px-1.5 py-0.5 rounded ml-1">DRAFT</span>}
                      {!p.active && <span className="text-[10px] text-gray-600 bg-gray-100 px-1.5 py-0.5 rounded ml-1">EXPIRED</span>}
                    </p>
                    <p className="text-xs text-gray-500 truncate flex items-center gap-2 mt-0.5">
                      {typeof p.bedrooms === 'number' && p.bedrooms > 0 && (<><BedDouble size={10} /> {p.bedrooms} bed ·</>)}
                      {p.area && (<><MapPin size={10} />{p.area} ·</>)}
                      <span className="capitalize bg-slate-100 px-1.5 rounded">{p.property_type}</span>
                      {p.monthly_rent ? <span className="font-semibold text-emerald-700">· £{p.monthly_rent} pcm</span> : null}
                      <span>· {p.source}</span>
                    </p>
                  </div>
                  <div className="flex items-center gap-1">
                    <Link to={`/properties/${p.slug}`} target="_blank" className="text-xs text-emerald-600 hover:underline mr-2">View</Link>
                    {!p.active && (
                      <button onClick={() => reactivate(p)} className="p-2 text-gray-500 hover:text-emerald-600" title="Re-activate (extend 10 days)" data-testid={`reactivate-property-${p.slug}`}>
                        <RotateCcw size={14} />
                      </button>
                    )}
                    <button onClick={() => startEdit(p)} className="p-2 text-gray-500 hover:text-cyan-600" data-testid={`edit-property-${p.slug}`}>
                      <Pencil size={14} />
                    </button>
                    <button onClick={() => remove(p)} className="p-2 text-gray-500 hover:text-red-600" data-testid={`delete-property-${p.slug}`}>
                      <Trash2 size={14} />
                    </button>
                  </div>
                </li>
              ))}
            </ul>
          )}
        </div>

        {/* Editor panel */}
        <div className="bg-white rounded-xl border border-gray-200 p-5 h-fit lg:sticky lg:top-6">
          <h2 className="text-lg font-semibold text-slate-900 mb-4">
            {editing && editing.id ? 'Edit property' : editing ? 'Create property' : 'Select or create a property'}
          </h2>
          {!editing ? (
            <p className="text-sm text-gray-500">Click "New property" above, import a CSV, or pick one from the list to edit.</p>
          ) : (
            <form onSubmit={save} className="space-y-4" data-testid="property-form">
              <div>
                <Label>Title *</Label>
                <Input value={form.title} onChange={(e) => setForm((f) => ({ ...f, title: e.target.value }))} required placeholder="e.g. 2-bed Flat, Queen Street, Paisley" data-testid="property-title-input" />
              </div>
              <div>
                <Label>Address</Label>
                <Input value={form.address} onChange={(e) => setForm((f) => ({ ...f, address: e.target.value }))} placeholder="Street, town, postcode" />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label>Area / Postcode</Label>
                  <Input value={form.area} onChange={(e) => setForm((f) => ({ ...f, area: e.target.value }))} placeholder="PA1, Foxbar…" />
                </div>
                <div>
                  <Label>Bedrooms</Label>
                  <Input type="number" min="0" value={form.bedrooms} onChange={(e) => setForm((f) => ({ ...f, bedrooms: e.target.value }))} />
                </div>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label>Type</Label>
                  <Select value={form.property_type} onValueChange={(v) => setForm((f) => ({ ...f, property_type: v }))}>
                    <SelectTrigger><SelectValue /></SelectTrigger>
                    <SelectContent>
                      {PROPERTY_TYPES.map((c) => <SelectItem key={c} value={c} className="capitalize">{c}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label>Furnished</Label>
                  <Input value={form.furnished} onChange={(e) => setForm((f) => ({ ...f, furnished: e.target.value }))} placeholder="Furnished / Unfurnished / Part" />
                </div>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label>Monthly Rent (£)</Label>
                  <Input type="number" step="0.01" min="0" value={form.monthly_rent} onChange={(e) => setForm((f) => ({ ...f, monthly_rent: e.target.value }))} placeholder="750" />
                </div>
                <div>
                  <Label>Available From</Label>
                  <Input type="date" value={form.available_from} onChange={(e) => setForm((f) => ({ ...f, available_from: e.target.value }))} />
                </div>
              </div>
              <div>
                <Label>Description</Label>
                <Textarea rows={3} value={form.description} onChange={(e) => setForm((f) => ({ ...f, description: e.target.value }))} placeholder="Key features, area highlights…" />
              </div>
              <div>
                <Label>Listing URL (external)</Label>
                <Input value={form.listing_url} onChange={(e) => setForm((f) => ({ ...f, listing_url: e.target.value }))} placeholder="https://www.rightmove.co.uk/…" />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label>Agent / Landlord</Label>
                  <Input value={form.agent} onChange={(e) => setForm((f) => ({ ...f, agent: e.target.value }))} placeholder="Castle Residential" />
                </div>
                <div>
                  <Label>Source Site</Label>
                  <Input value={form.source_site} onChange={(e) => setForm((f) => ({ ...f, source_site: e.target.value }))} placeholder="Rightmove" />
                </div>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label>Contact Phone</Label>
                  <Input value={form.contact_phone} onChange={(e) => setForm((f) => ({ ...f, contact_phone: e.target.value }))} />
                </div>
                <div>
                  <Label>Contact Email</Label>
                  <Input type="email" value={form.contact_email} onChange={(e) => setForm((f) => ({ ...f, contact_email: e.target.value }))} />
                </div>
              </div>
              <div>
                <Label>Image URL</Label>
                <div className="flex gap-2 mt-1">
                  <Input value={form.image_url} onChange={(e) => setForm((f) => ({ ...f, image_url: e.target.value }))} placeholder="https://… or /api/files/…" className="flex-1" />
                  <Button type="button" variant="outline" onClick={() => setMediaOpen(true)} data-testid="property-image-library-btn">
                    <Library size={14} className="mr-1" />Library
                  </Button>
                </div>
                {form.image_url && (
                  <img src={form.image_url.startsWith('http') ? form.image_url : `${BACKEND_URL}${form.image_url}`} alt="" className="mt-2 h-28 w-full object-cover rounded-lg"
                    onError={(e) => (e.target.style.display = 'none')} />
                )}
                <p className="text-xs text-slate-400 mt-1.5">Leave blank to fall back to Paisley skyline default.</p>
              </div>
              <div>
                <Label>URL slug</Label>
                <Input value={form.slug} onChange={(e) => setForm((f) => ({ ...f, slug: e.target.value }))} placeholder="auto-from-title" />
              </div>
              <div className="flex gap-6">
                <label className="flex items-center gap-2 cursor-pointer">
                  <input type="checkbox" checked={form.featured} onChange={(e) => setForm((f) => ({ ...f, featured: e.target.checked }))} className="w-4 h-4 rounded" data-testid="property-featured-checkbox" />
                  <span className="text-sm">Featured</span>
                </label>
                <label className="flex items-center gap-2 cursor-pointer">
                  <input type="checkbox" checked={form.published} onChange={(e) => setForm((f) => ({ ...f, published: e.target.checked }))} className="w-4 h-4 rounded" />
                  <span className="text-sm">Published</span>
                </label>
              </div>
              <div className="flex gap-2 pt-2">
                <Button type="submit" disabled={saving} className="bg-emerald-600 hover:bg-emerald-700 flex-1" data-testid="property-save-btn">
                  {saving ? 'Saving…' : (editing.id ? 'Update property' : 'Create property')}
                </Button>
                <Button type="button" variant="outline" onClick={cancelEdit}>Cancel</Button>
              </div>
            </form>
          )}
        </div>
      </div>

      <MediaPicker
        open={mediaOpen}
        onClose={() => setMediaOpen(false)}
        title="Select property image"
        onSelect={(url) => setForm((f) => ({ ...f, image_url: url }))}
      />
    </div>
  );
};

export default PropertiesManager;
